import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, Archive, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useShipmentSessions,
  useCreateShipmentSession,
} from "@/hooks/use-shipment-counting";
import { toast } from "sonner";

export function ShipmentSessionsPage() {
  const [tab, setTab] = useState<"active" | "archived">("active");
  const { data: sessions, isLoading } = useShipmentSessions(tab);
  const createSession = useCreateShipmentSession();
  const navigate = useNavigate();

  async function handleCreate() {
    try {
      const s = await createSession.mutateAsync({});
      navigate({ to: "/warehouse/brojanje-posiljki/$sessionId", params: { sessionId: s.id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  return (
    <div>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Brojanje pošiljki</h1>
          <Button size="sm" onClick={handleCreate} disabled={createSession.isPending}>
            <Plus className="h-4 w-4 mr-1" /> Nova sesija
          </Button>
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList>
            <TabsTrigger value="active">Aktivne</TabsTrigger>
            <TabsTrigger value="archived">Arhivirane</TabsTrigger>
          </TabsList>
        </Tabs>
        {isLoading && <p className="text-sm text-muted-foreground">Učitavanje…</p>}
        {!isLoading && (sessions?.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground">
            {tab === "active" ? "Nema aktivnih sesija." : "Nema arhiviranih sesija."}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sessions?.map((s: any) => {
            const uredno = s.progress.missing === 0 && s.progress.extra === 0;
            return (
              <Link
                key={s.id}
                to="/warehouse/brojanje-posiljki/$sessionId"
                params={{ sessionId: s.id }}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors block"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{s.name}</span>
                  {s.status === "archived" ? (
                    <Archive className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Package className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {s.firms.length > 0 ? s.firms.join(", ") : "Bez uvezenih najava"}
                </p>
                <div className="mt-2 text-sm">
                  {s.progress.scanned} / {s.progress.total} skenirano
                  {!uredno && s.progress.total > 0 && (
                    <span className="text-destructive ml-2">
                      {s.progress.missing > 0 && `fali ${s.progress.missing}`}
                      {s.progress.missing > 0 && s.progress.extra > 0 && ", "}
                      {s.progress.extra > 0 && `višak ${s.progress.extra}`}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
